import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckOutlined, CloseOutlined, FolderOutlined, MessageOutlined, PushpinOutlined } from '@ant-design/icons';

import { Button, Tabs, Tag } from '../ds';
import { ApiError, friendlyError } from '../../lib/api';
import {
  OPENING_TASKS_KEYS,
  openingTasksApi,
  type MyOpeningTasksDto,
  type MyRoomDto,
  type ThreadFileDto,
} from '../../lib/api/openingTasks';
import { MomentsList } from './MomentsList';
import { RoomChat } from './RoomChat';
import { FileChip, PhotoLightbox, PhotoThumb, VideoBox } from './ThreadFiles';
import { fmtDate, isRoomRejected, ROOM_STAGES, roomStageColor, roomStageLabel } from './utils';

type RoomTab = 'chat' | 'moments' | 'files';

/**
 * Помещение партнёра (заявка «Осмотр помещения» — та же, что у куратора в
 * CRM → Обращения): стадии заявки и вкладки «Переписка», «Моменты»,
 * «Фото и файлы». Открытие переписки помечает её прочитанной.
 */
export function RoomDetail({ roomId, room, onClose }: { roomId: string; room: MyRoomDto | null; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [tab, setTab] = useState<RoomTab>('chat');
  const [flashMsgId, setFlashMsgId] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<number | null>(null);

  const q = useQuery({
    queryKey: OPENING_TASKS_KEYS.thread(roomId),
    queryFn: () => openingTasksApi.thread(roomId),
    retry: (n, e) => n < 1 && !(e instanceof ApiError && e.status < 500),
    staleTime: 15_000,
    // Ответ куратора подтянется сам, пока карточка открыта.
    refetchInterval: 60_000,
  });
  const thread = q.data;

  // Сервер пометил переписку прочитанной — гасим бейдж помещения сразу.
  const unreadBefore = room?.unread ?? 0;
  const loaded = q.isSuccess;
  useEffect(() => {
    if (!loaded || unreadBefore === 0) return;
    qc.setQueryData<MyOpeningTasksDto>(OPENING_TASKS_KEYS.myTasks, (prev) =>
      prev ? { ...prev, rooms: prev.rooms.map((r) => (r.id === roomId ? { ...r, unread: 0 } : r)) } : prev,
    );
  }, [loaded, unreadBefore, roomId, qc]);

  const allFiles = useMemo(() => (thread?.messages ?? []).flatMap((m) => m.files), [thread]);
  const photos = useMemo(() => allFiles.filter((f) => f.kind === 'photo'), [allFiles]);
  const photoUrls = useMemo(() => photos.map((f) => f.url), [photos]);
  const openPhoto = (fileId: string): void => {
    const i = photos.findIndex((f) => f.id === fileId);
    if (i >= 0) setLightbox(i);
  };

  const stageCode = thread?.stageCode ?? room?.stageCode ?? null;
  const stageLabel = roomStageLabel(stageCode, thread?.stageLabel ?? room?.stageLabel ?? null, room?.status);
  const rejected = isRoomRejected(stageCode, room?.status);
  const address = thread?.address ?? room?.address ?? 'Помещение';
  const openMoments = thread ? thread.moments.filter((m) => m.status === 'open').length : room?.openMoments ?? 0;

  const subParts = [
    room?.createdAt ? `заявка от ${fmtDate(room.createdAt)}` : null,
    room?.score != null ? `балл осмотра ${Math.round(room.score)}%` : null,
    room?.verdict ?? null,
  ].filter(Boolean);

  return (
    <>
      <div className="fk-ot-dhead">
        <div style={{ minWidth: 0 }}>
          <h3>{address}</h3>
          {subParts.length ? <div className="fk-ot-lsub">{subParts.join(' · ')}</div> : null}
        </div>
        <div className="fk-ot-dhead-r">
          <Tag color={roomStageColor(stageCode, room?.status)}>{stageLabel}</Tag>
          <Button variant="text" size="sm" icon={<CloseOutlined />} onClick={onClose} aria-label="Закрыть помещение">
            Закрыть
          </Button>
        </div>
      </div>

      <RoomSteps stageCode={stageCode} rejected={rejected} />

      <Tabs<RoomTab>
        value={tab}
        onChange={(k) => {
          setTab(k);
          setFlashMsgId(null);
        }}
        items={[
          { key: 'chat', label: 'Переписка', icon: <MessageOutlined /> },
          { key: 'moments', label: 'Моменты', icon: <PushpinOutlined />, badge: openMoments || null, badgeColor: 'var(--fk-orange)' },
          { key: 'files', label: 'Фото и файлы', icon: <FolderOutlined />, badge: allFiles.length || null },
        ]}
      />

      {q.isLoading ? (
        <div className="fk-ot-loading">Загружаем переписку…</div>
      ) : q.error || !thread ? (
        <div className="fk-ot-callout" data-tone="error">
          <CloseOutlined />
          <div style={{ minWidth: 0 }}>
            Не удалось открыть переписку: {friendlyError(q.error)}{' '}
            <button type="button" className="fk-ot-linkbtn" onClick={() => void q.refetch()}>
              Повторить
            </button>
          </div>
        </div>
      ) : tab === 'chat' ? (
        <RoomChat roomId={roomId} thread={thread} rejected={rejected} flashMsgId={flashMsgId} onOpenPhoto={openPhoto} />
      ) : tab === 'moments' ? (
        <MomentsList
          roomId={roomId}
          moments={thread.moments}
          onShowMessage={(commentId) => {
            setTab('chat');
            setFlashMsgId(commentId);
          }}
        />
      ) : (
        <RoomFiles files={allFiles} onOpenPhoto={openPhoto} />
      )}

      <PhotoLightbox urls={photoUrls} index={lightbox} onChange={setLightbox} onClose={() => setLightbox(null)} />
    </>
  );
}

/** Стадии заявки «Осмотр помещения» (как в CRM); отказ — коротко «Поступила → Куратор смотрит → Не подходит». */
function RoomSteps({ stageCode, rejected }: { stageCode: string | null; rejected: boolean }): JSX.Element | null {
  if (rejected) {
    return (
      <div className="fk-ot-steps">
        {ROOM_STAGES.slice(0, 2).map((s) => (
          <span key={s.code} className="fk-ot-step" data-done="true">
            <span className="fk-ot-num" data-done="true">
              <CheckOutlined />
            </span>
            {s.label}
          </span>
        ))}
        <span className="fk-ot-step" data-bad="true">
          <span className="fk-ot-num">
            <CloseOutlined />
          </span>
          Не подходит
        </span>
      </div>
    );
  }
  const idx = ROOM_STAGES.findIndex((s) => s.code === stageCode);
  if (idx < 0) return null;
  return (
    <div className="fk-ot-steps" aria-label={`Стадия заявки: ${ROOM_STAGES[idx]!.label}`}>
      {ROOM_STAGES.map((s, i) => (
        <span key={s.code} className="fk-ot-step" data-done={i < idx} data-now={i === idx}>
          <span className="fk-ot-num" data-done={i < idx}>
            {i < idx ? <CheckOutlined /> : i + 1}
          </span>
          {s.label}
        </span>
      ))}
    </div>
  );
}

/** Все вложения переписки в одном месте: фото сеткой, видео, планировки и файлы. */
function RoomFiles({ files, onOpenPhoto }: { files: ThreadFileDto[]; onOpenPhoto: (fileId: string) => void }): JSX.Element {
  const photos = files.filter((f) => f.kind === 'photo');
  const videos = files.filter((f) => f.kind === 'video');
  const docs = files.filter((f) => f.kind !== 'photo' && f.kind !== 'video');
  if (!files.length) {
    return <div className="fk-ot-note">Файлов пока нет. Фото, видео и планировки из переписки соберутся здесь.</div>;
  }
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {photos.length ? (
        <div className="fk-ot-gallery">
          {photos.map((f) => (
            <PhotoThumb key={f.id} file={f} onOpen={() => onOpenPhoto(f.id)} />
          ))}
        </div>
      ) : null}
      {videos.length ? (
        <div className="fk-ot-videos">
          {videos.map((f) => (
            <VideoBox key={f.id} file={f} />
          ))}
        </div>
      ) : null}
      {docs.length ? (
        <div className="fk-ot-atts">
          {docs.map((f) => (
            <FileChip key={f.id} file={f} />
          ))}
        </div>
      ) : null}
      <div className="fk-ot-note">Все фото, видео и планировки из переписки — в одном месте. Куратор видит их в карточке заявки в CRM.</div>
    </div>
  );
}
